import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createNotificationApi,
  deleteNotificationApi,
  getNotificationCountApi,
  getNotificationTypesApi,
  getNotificationsApi,
  markNotificationAsReadApi,
  saveFcmTokenAPI,
  uploadNotificationImageApi,
} from "../../api/ecommerce/NotificationsApi";

const initialState = {
  fileUploadRespone: "",
  notifications: [],
  notificationTypes: [],
  notificationCount: 0,
  notificationDetails: null,
  latestRealtimeNotification: null,
  realtimeNotificationSequence: 0,
  uploading: false,
  loading: false,
  createLoading: false,
  deleteLoading: false,
  readLoading: false,
  typesLoading: false,
  countLoading: false,
  openToggler: false,
};

export const uploadNotificationImage = createAsyncThunk(
  "adminNotification/uploadNotificationImage",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await uploadNotificationImageApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  }
);

export const getNotifications = createAsyncThunk(
  "adminNotification/getNotifications",
  async (type, { rejectWithValue }) => {
    try {
      const response = await getNotificationsApi(type);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  }
);

export const createNotification = createAsyncThunk(
  "adminNotification/createNotification",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createNotificationApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  }
);

export const getNotificationTypes = createAsyncThunk(
  "adminNotification/getNotificationTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getNotificationTypesApi();
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  }
);

export const getNotificationCount = createAsyncThunk(
  "adminNotification/getNotificationCount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getNotificationCountApi();
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  "adminNotification/markNotificationAsRead",
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await markNotificationAsReadApi(notificationId);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  }
);

export const deleteNotification = createAsyncThunk(
  "adminNotification/deleteNotification",
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await deleteNotificationApi(notificationId);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message,
      });
    }
  }
);

export const saveFcmToken = createAsyncThunk(
  "adminNotification/saveFcmToken",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await saveFcmTokenAPI(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.message || err?.data?.message,
      });
    }
  }
);

const adminNotifictionSlice = createSlice({
  name: "adminNotification",
  initialState,
  reducers: {
    setToggler: (state, action) => {
      state.openToggler = action.payload;
    },
    prependNotification: (state, action) => {
      const incomingNotification = action.payload;

      if (!incomingNotification?.notificationId) {
        return;
      }

      state.latestRealtimeNotification = incomingNotification;
      state.realtimeNotificationSequence += 1;

      const alreadyExists = Array.isArray(state.notifications)
        ? state.notifications.some(
            (item) =>
              Number(item.notificationId) ===
              Number(incomingNotification.notificationId)
          )
        : false;

      if (alreadyExists) {
        state.notifications = state.notifications.map((item) =>
          Number(item.notificationId) === Number(incomingNotification.notificationId)
            ? { ...item, ...incomingNotification }
            : item
        );
      } else {
        state.notifications = Array.isArray(state.notifications)
          ? [incomingNotification, ...state.notifications]
          : [incomingNotification];

        if (incomingNotification.read === false) {
          state.notificationCount += 1;
        }
      }
    },
  },
  extraReducers: (builder) => {
    //file upload
    builder.addCase(uploadNotificationImage.pending, (state) => {
      state.uploading = true;
    });
    builder.addCase(uploadNotificationImage.fulfilled, (state, action) => {
      state.fileUploadRespone = action.payload.response;
      state.uploading = false;
    });
    builder.addCase(uploadNotificationImage.rejected, (state) => {
      state.uploading = false;
    });

    //get notifications
    builder.addCase(getNotifications.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getNotifications.fulfilled, (state, action) => {
      state.notifications = action.payload?.response || [];
      state.loading = false;
    });
    builder.addCase(getNotifications.rejected, (state) => {
      state.loading = false;
    });

    //create notification
    builder.addCase(createNotification.pending, (state) => {
      state.createLoading = true;
    });
    builder.addCase(createNotification.fulfilled, (state, action) => {
      const createdNotification = action.payload?.response;
      state.notificationDetails = createdNotification || null;
      if (createdNotification) {
        state.notifications = Array.isArray(state.notifications)
          ? [createdNotification, ...state.notifications]
          : [createdNotification];
        if (createdNotification.read === false) {
          state.notificationCount += 1;
        }
      }
      state.createLoading = false;
    });
    builder.addCase(createNotification.rejected, (state) => {
      state.createLoading = false;
    });

    //get notification types
    builder.addCase(getNotificationTypes.pending, (state) => {
      state.typesLoading = true;
    });
    builder.addCase(getNotificationTypes.fulfilled, (state, action) => {
      state.notificationTypes = action.payload?.response || [];
      state.typesLoading = false;
    });
    builder.addCase(getNotificationTypes.rejected, (state) => {
      state.typesLoading = false;
    });

    //get notification count
    builder.addCase(getNotificationCount.pending, (state) => {
      state.countLoading = true;
    });
    builder.addCase(getNotificationCount.fulfilled, (state, action) => {
      state.notificationCount = action.payload?.response ?? 0;
      state.countLoading = false;
    });
    builder.addCase(getNotificationCount.rejected, (state) => {
      state.countLoading = false;
    });

    //mark notification as read
    builder.addCase(markNotificationAsRead.pending, (state) => {
      state.readLoading = true;
    });
    builder.addCase(markNotificationAsRead.fulfilled, (state, action) => {
      const { arg } = action.meta;
      const existingNotification = Array.isArray(state.notifications)
        ? state.notifications.find(
            (item) => Number(item.notificationId) === Number(arg)
          )
        : null;

      state.notificationDetails = action.payload?.response || null;
      state.notifications = Array.isArray(state.notifications)
        ? state.notifications.map((item) =>
            Number(item.notificationId) === Number(arg)
              ? { ...item, read: true }
              : item
          )
        : [];

      if (existingNotification && existingNotification.read === false && state.notificationCount > 0) {
        state.notificationCount -= 1;
      }

      state.readLoading = false;
    });
    builder.addCase(markNotificationAsRead.rejected, (state) => {
      state.readLoading = false;
    });

    //delete notification
    builder.addCase(deleteNotification.pending, (state) => {
      state.deleteLoading = true;
    });
    builder.addCase(deleteNotification.fulfilled, (state, action) => {
      const { arg } = action.meta;
      const deletedNotification = Array.isArray(state.notifications)
        ? state.notifications.find(
            (item) => Number(item.notificationId) === Number(arg)
          )
        : null;

      state.notifications = Array.isArray(state.notifications)
        ? state.notifications.filter(
            (item) => Number(item.notificationId) !== Number(arg)
          )
        : [];

      if (deletedNotification && deletedNotification.read === false && state.notificationCount > 0) {
        state.notificationCount -= 1;
      }

      state.notificationDetails = action.payload?.response || null;
      state.deleteLoading = false;
    });
    builder.addCase(deleteNotification.rejected, (state) => {
      state.deleteLoading = false;
    });
  },
});

export const { setToggler, prependNotification } = adminNotifictionSlice.actions;
export default adminNotifictionSlice.reducer;
